import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plug, Settings, RefreshCw, CheckCircle2, XCircle, AlertTriangle, 
  Clock, Play, Pause, Eye, FileText, Database, Shield, Key,
  Globe, Building2, Scale, Wallet, FileSignature, Users, BarChart3,
  Loader2, ChevronRight, History, Activity, Download
} from 'lucide-react';

// Icon mapping for connectors
const CONNECTOR_ICONS = {
  sei: FileText,
  pncp: Building2,
  govbr: Users,
  icpbrasil: FileSignature,
  siafi: Wallet,
  sigef: BarChart3,
  afm: Database,
  jurisprudencia: Scale,
  contracts: FileText,
  sigplan: BarChart3
};

// Phase colors
const PHASE_COLORS = {
  1: 'bg-green-100 text-green-700',
  2: 'bg-blue-100 text-blue-700',
  3: 'bg-blue-100 text-blue-700'
};

const STATUS_STYLES = {
  ACTIVE: { color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  INACTIVE: { color: 'bg-gray-100 text-gray-700', icon: Pause },
  ERROR: { color: 'bg-red-100 text-red-700', icon: XCircle },
  PENDING_CONFIG: { color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle },
  MAINTENANCE: { color: 'bg-orange-100 text-orange-700', icon: Clock }
};

const MODE_STYLES = {
  MOCK: { color: 'bg-blue-100 text-blue-700', label: 'Simulação' },
  HOMOLOGATION: { color: 'bg-yellow-100 text-yellow-700', label: 'Homologação' },
  PRODUCTION: { color: 'bg-green-100 text-green-700', label: 'Produção' }
};

export const IntegrationsPage = () => {
  const { api } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [testingConnection, setTestingConnection] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [credentials, setCredentials] = useState({});
  const [settings, setSettings] = useState({});
  const [mode, setMode] = useState('MOCK');

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/integrations/status');
      setIntegrations(response.data);
    } catch (error) {
      console.error('Error loading integrations:', error);
      toast.error('Erro ao carregar integrações');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async (connectorId = null) => {
    try {
      const params = connectorId ? { connector_id: connectorId } : {};
      const [logsRes, historyRes] = await Promise.all([
        api.get('/integrations/logs', { params: { ...params, limit: 50 } }),
        api.get('/integrations/sync-history', { params: { ...params, limit: 20 } })
      ]);
      setLogs(logsRes.data);
      setSyncHistory(historyRes.data);
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  };

  const handleTestConnection = async (connectorId) => {
    try {
      setTestingConnection(connectorId);
      const response = await api.post(`/integrations/${connectorId}/test`);
      if (response.data.success) {
        toast.success(`Conexão testada com sucesso (${response.data.mode || 'OK'})`);
      } else {
        toast.error(response.data.error || 'Falha no teste de conexão');
      }
    } catch (error) {
      toast.error('Erro ao testar conexão');
    } finally {
      setTestingConnection(null);
    }
  };

  const handleSync = async (connectorId) => {
    try {
      setSyncing(connectorId);
      const response = await api.post(`/integrations/${connectorId}/sync`);
      if (response.data.success) {
        toast.success(`Sincronização concluída: ${response.data.records_processed || 0} registros`);
        loadIntegrations();
      } else {
        toast.error(response.data.error || 'Falha na sincronização');
      }
    } catch (error) {
      toast.error('Erro na sincronização');
    } finally {
      setSyncing(null);
    }
  };

  const handleToggleEnabled = async (connector) => {
    try {
      const endpoint = connector.enabled 
        ? `/integrations/${connector.connector_id}/disable`
        : `/integrations/${connector.connector_id}/enable`;
      
      await api.post(endpoint);
      toast.success(connector.enabled ? 'Integração desativada' : 'Integração ativada');
      loadIntegrations();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const openConfigDialog = async (connector) => {
    try {
      const response = await api.get(`/integrations/${connector.connector_id}/config`);
      setSelectedConnector(response.data);
      setCredentials(response.data.config?.credentials || {});
      setSettings(response.data.config?.settings || {});
      setMode(response.data.config?.mode || 'MOCK');
      setConfigDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar configuração');
    }
  };

  const saveConfig = async () => {
    try {
      await api.put(`/integrations/${selectedConnector.connector_id}/config`, {
        credentials,
        settings,
        mode
      });
      toast.success('Configuração salva com sucesso');
      setConfigDialogOpen(false);
      loadIntegrations();
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    }
  };

  const openLogsDialog = async (connector) => {
    setSelectedConnector(connector);
    await loadLogs(connector.connector_id);
    setLogsDialogOpen(true);
  };

  const filteredIntegrations = activeTab === 'all' 
    ? integrations 
    : integrations.filter(i => i.phase === parseInt(activeTab));

  const Icon = ({ name }) => {
    const IconComponent = CONNECTOR_ICONS[name] || Plug;
    return <IconComponent className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="integrations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integrações</h1>
          <p className="text-gray-600">Gerencie as integrações com sistemas governamentais</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => loadIntegrations()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button variant="outline" onClick={() => { setSelectedConnector(null); loadLogs(); setLogsDialogOpen(true); }}>
            <History className="w-4 h-4 mr-2" />
            Logs Gerais
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Plug className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{integrations.length}</p>
                <p className="text-sm text-gray-600">Total de Conectores</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{integrations.filter(i => i.enabled).length}</p>
                <p className="text-sm text-gray-600">Ativos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{integrations.filter(i => i.mode === 'MOCK').length}</p>
                <p className="text-sm text-gray-600">Em Simulação</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{integrations.filter(i => i.status === 'ERROR').length}</p>
                <p className="text-sm text-gray-600">Com Erro</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs by Phase */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">Todos</TabsTrigger>
          <TabsTrigger value="1">Fase 1 (SEI, PNCP, GovBR, ICP)</TabsTrigger>
          <TabsTrigger value="2">Fase 2 (Financeiros)</TabsTrigger>
          <TabsTrigger value="3">Fase 3 (Jurídico, Contratos, PPA)</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredIntegrations.map((connector) => {
              const StatusIcon = STATUS_STYLES[connector.status]?.icon || AlertTriangle;
              const statusStyle = STATUS_STYLES[connector.status] || STATUS_STYLES.PENDING_CONFIG;
              const modeStyle = MODE_STYLES[connector.mode] || MODE_STYLES.MOCK;
              
              return (
                <Card key={connector.connector_id} className="relative overflow-hidden" data-testid={`connector-${connector.connector_id}`}>
                  {/* Phase indicator */}
                  <div className={`absolute top-0 right-0 px-2 py-1 text-xs font-medium rounded-bl-lg ${PHASE_COLORS[connector.phase]}`}>
                    Fase {connector.phase}
                  </div>
                  
                  <CardHeader className="pb-2">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${connector.enabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
                        <Icon name={connector.connector_id} />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg">{connector.name}</CardTitle>
                        <CardDescription className="text-sm mt-1">{connector.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    {/* Status badges */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Badge className={statusStyle.color}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {connector.status}
                      </Badge>
                      <Badge className={modeStyle.color}>
                        {modeStyle.label}
                      </Badge>
                      <Badge variant="outline">v{connector.version}</Badge>
                    </div>
                    
                    {/* Last sync info */}
                    {connector.last_sync && (
                      <p className="text-xs text-gray-500 mb-3">
                        Última sincronização: {new Date(connector.last_sync).toLocaleString('pt-BR')}
                      </p>
                    )}
                    
                    {/* Actions */}
                    <div className="flex items-center justify-between pt-3 border-t">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={connector.enabled}
                          onCheckedChange={() => handleToggleEnabled(connector)}
                          data-testid={`toggle-${connector.connector_id}`}
                        />
                        <span className="text-sm text-gray-600">
                          {connector.enabled ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => openConfigDialog(connector)}
                          data-testid={`config-${connector.connector_id}`}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleTestConnection(connector.connector_id)}
                          disabled={testingConnection === connector.connector_id}
                        >
                          {testingConnection === connector.connector_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleSync(connector.connector_id)}
                          disabled={syncing === connector.connector_id || !connector.enabled}
                        >
                          {syncing === connector.connector_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => openLogsDialog(connector)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Configuration Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configurar {selectedConnector?.name}
            </DialogTitle>
            <DialogDescription>
              Configure as credenciais e parâmetros da integração
            </DialogDescription>
          </DialogHeader>
          
          {selectedConnector && (
            <div className="space-y-6">
              {/* Mode Selection */}
              <div className="space-y-2">
                <Label>Modo de Operação</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MOCK">Simulação (Mock)</SelectItem>
                    <SelectItem value="HOMOLOGATION">Homologação</SelectItem>
                    <SelectItem value="PRODUCTION">Produção</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  {mode === 'MOCK' && 'Usa dados simulados para demonstração. Não requer credenciais.'}
                  {mode === 'HOMOLOGATION' && 'Conecta ao ambiente de testes do sistema externo.'}
                  {mode === 'PRODUCTION' && 'Conecta ao ambiente de produção. Use com cuidado.'}
                </p>
              </div>
              
              {/* Credentials */}
              {selectedConnector.required_credentials?.length > 0 && (
                <div className="space-y-4">
                  <Label className="flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Credenciais
                  </Label>
                  {selectedConnector.required_credentials.map((cred) => (
                    <div key={cred.key} className="space-y-1">
                      <Label className="text-sm">
                        {cred.label}
                        {cred.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <Input
                        type={cred.type === 'password' ? 'password' : 'text'}
                        value={credentials[cred.key] || ''}
                        onChange={(e) => setCredentials({...credentials, [cred.key]: e.target.value})}
                        placeholder={cred.description}
                      />
                      <p className="text-xs text-gray-500">{cred.description}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Info about mock mode */}
              {mode === 'MOCK' && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Modo Simulação:</strong> Esta integração usará dados fictícios para demonstração. 
                    Ideal para apresentações comerciais e testes de interface.
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={saveConfig}>
              Salvar Configuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logs Dialog */}
      <Dialog open={logsDialogOpen} onOpenChange={setLogsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Logs de Integração {selectedConnector ? `- ${selectedConnector.name}` : ''}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="logs" className="mt-4">
            <TabsList>
              <TabsTrigger value="logs">Logs de Auditoria</TabsTrigger>
              <TabsTrigger value="sync">Histórico de Sincronização</TabsTrigger>
            </TabsList>
            
            <TabsContent value="logs" className="mt-4">
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Data/Hora</th>
                      <th className="px-3 py-2 text-left">Conector</th>
                      <th className="px-3 py-2 text-left">Ação</th>
                      <th className="px-3 py-2 text-left">Modo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-600">
                          {new Date(log.timestamp).toLocaleString('pt-BR')}
                        </td>
                        <td className="px-3 py-2">{log.connector_name}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline">{log.action}</Badge>
                        </td>
                        <td className="px-3 py-2">
                          <Badge className={MODE_STYLES[log.mode]?.color || 'bg-gray-100'}>
                            {log.mode}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                    {logs.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-3 py-8 text-center text-gray-500">
                          Nenhum log encontrado
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>
            
            <TabsContent value="sync" className="mt-4">
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Data/Hora</th>
                      <th className="px-3 py-2 text-left">Conector</th>
                      <th className="px-3 py-2 text-left">Tipo</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-right">Registros</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncHistory.map((sync, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-600">
                          {new Date(sync.started_at).toLocaleString('pt-BR')}
                        </td>
                        <td className="px-3 py-2">{sync.connector_id}</td>
                        <td className="px-3 py-2">{sync.sync_type}</td>
                        <td className="px-3 py-2">
                          <Badge className={sync.status === 'SUCCESS' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {sync.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right">{sync.records_processed}</td>
                      </tr>
                    ))}
                    {syncHistory.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-3 py-8 text-center text-gray-500">
                          Nenhuma sincronização encontrada
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntegrationsPage;
